import type { SclNode } from "./jsonContracts";

const decoder = new TextDecoder("utf-8");

// ── Detección de formato ──────────────────────────────────────────────────────
/** Los primeros 4 bytes de un xlsx/xls son la firma ZIP (PK\x03\x04). */
function isXlsx(bytes: Uint8Array): boolean {
  return bytes[0]===0x50 && bytes[1]===0x4b && bytes[2]===0x03 && bytes[3]===0x04;
}

// ── Parser xlsx con SheetJS ───────────────────────────────────────────────────
// Detección de columnas por nombre de encabezado (ES + EN). Devuelve los
// índices de columna para señal / nodo MMS / dataset, o null si la fila no
// parece un encabezado (entonces se usa el orden posicional por defecto).
function normHdr(s: unknown): string {
  return String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}
const HDR_SIGNAL  = ["nombre senal","nombre de senal","senal","signal","signal name","nombre","name","tag","descripcion","description"];
const HDR_MMSNODE = ["nodo mms","nodo logico","nodo","mms node","mms","logical node","node"];
const HDR_DATASET = ["dataset","data set","conjunto de datos"];
function mapHeader(cells: unknown[]): { signal: number; mms: number; ds: number } | null {
  let iSig = -1, iMms = -1, iDs = -1;
  cells.forEach((c, idx) => {
    const v = normHdr(c);
    if (!v) return;
    if (iSig < 0 && HDR_SIGNAL.includes(v))  iSig = idx;
    if (iMms < 0 && HDR_MMSNODE.includes(v)) iMms = idx;
    if (iDs  < 0 && HDR_DATASET.includes(v)) iDs  = idx;
  });
  // Es encabezado si reconocimos al menos la columna de señal o la de nodo.
  if (iSig < 0 && iMms < 0) return null;
  return { signal: iSig, mms: iMms, ds: iDs };
}

async function parseXlsxBytes(
  bytes: Uint8Array,
): Promise<Array<{signal: string; mmsNode?: string; dataset?: string}>> {
  const XLSX = await import("xlsx");
  const wb   = XLSX.read(bytes, { type: "array" });
  const ws   = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, defval: "" });

  const result: Array<{signal: string; mmsNode?: string; dataset?: string}> = [];
  let start = 0;
  // Mapa de columnas por nombre; si no hay encabezado, orden posicional 0/1/2.
  let map = { signal: 0, mms: 1, ds: 2 };
  if (rows.length) {
    const hdr = mapHeader(rows[0]);
    if (hdr) {
      start = 1;
      map = { signal: hdr.signal >= 0 ? hdr.signal : 0,
              mms:    hdr.mms    >= 0 ? hdr.mms    : (hdr.signal === 1 ? 0 : 1),
              ds:     hdr.ds     >= 0 ? hdr.ds     : 2 };
    }
  }
  for (let i = start; i < rows.length; i++) {
    const row    = rows[i];
    const signal = String(row[map.signal] ?? "").trim();
    if (!signal) continue;
    const rawMms = String(row[map.mms] ?? "").trim();
    const mmsNode = rawMms && /[./]/.test(rawMms) ? rawMms : undefined;
    const rawDs  = String(row[map.ds] ?? "").trim();
    const dataset = rawDs || undefined;   // columna Dataset (opcional)
    result.push({ signal, mmsNode, dataset });
  }
  return result;
}

// ── Parser CSV / TXT ──────────────────────────────────────────────────────────
function parseCsvBytes(bytes: Uint8Array): Array<{signal: string; mmsNode?: string; dataset?: string}> {
  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    // Fallback para archivos guardados con encoding Windows
    text = new TextDecoder("windows-1252").decode(bytes);
  }
  const rows  = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  let start = 0;
  let map = { signal: 0, mms: 1, ds: 2 };
  if (rows.length) {
    const first = rows[0].split(/[,;\t]/).map(c => c.trim().replace(/^"|"$/g, ""));
    const hdr = mapHeader(first);
    if (hdr) {
      start = 1;
      map = { signal: hdr.signal >= 0 ? hdr.signal : 0,
              mms:    hdr.mms    >= 0 ? hdr.mms    : (hdr.signal === 1 ? 0 : 1),
              ds:     hdr.ds     >= 0 ? hdr.ds     : 2 };
    }
  }
  const result: Array<{signal: string; mmsNode?: string; dataset?: string}> = [];
  for (let i = start; i < rows.length; i++) {
    const cols   = rows[i].split(/[,;\t]/).map(c => c.trim().replace(/^"|"$/g, ""));
    const signal = cols[map.signal];
    if (!signal) continue;
    const rawMms = cols[map.mms];
    const mmsNode = rawMms && /[./]/.test(rawMms) ? rawMms : undefined;
    const dataset = cols[map.ds] || undefined;   // columna Dataset (opcional)
    result.push({ signal, mmsNode, dataset });
  }
  return result;
}

// ── Función principal — detecta formato automáticamente ───────────────────────
export async function parseSignalList(
  bytes: Uint8Array,
  filename = "",
): Promise<Array<{signal: string; mmsNode?: string; dataset?: string}>> {
  if (isXlsx(bytes)) return parseXlsxBytes(bytes);
  return parseCsvBytes(bytes);
}

// ── Parser SCL (sin cambios) ──────────────────────────────────────────────────
export function parseSclNodes(bytes: Uint8Array): SclNode[] {
  const xml   = decoder.decode(bytes);
  const nodes: SclNode[] = [];
  const seen  = new Set<string>();

  // Etapa 2 PROMPT UNIVERSAL v2: recorrer IED > LDevice > LN0 > DataSet > FCDA
  const iedRe = /<IED\b[^>]*\bname="([^"]+)"/g;
  let iedM: RegExpExecArray|null;

  while ((iedM = iedRe.exec(xml))) {
    const iedName  = iedM[1];
    const iedEnd   = xml.indexOf("</IED>", iedM.index);
    const iedBlock = iedEnd>0 ? xml.slice(iedM.index, iedEnd) : xml.slice(iedM.index);

    const ldRe = /<LDevice\b[^>]*\binst="([^"]+)"/g;
    let ldM: RegExpExecArray|null;

    while ((ldM = ldRe.exec(iedBlock))) {
      const ldInst   = ldM[1];
      const ldEnd    = iedBlock.indexOf("</LDevice>", ldM.index);
      const ldBlock  = ldEnd>0 ? iedBlock.slice(ldM.index, ldEnd) : iedBlock.slice(ldM.index);

      // Buscar DataSets (pueden estar en LN0 o directamente en LDevice)
      const dsRe = /<DataSet\b[^>]*\bname="([^"]+)"[^>]*>([\s\S]*?)<\/DataSet>/g;
      let dsM: RegExpExecArray|null;

      while ((dsM = dsRe.exec(ldBlock))) {
        const datasetName = dsM[1];
        const dsBody      = dsM[2];

        // Extraer cada FCDA del DataSet
        const fcdaRe = /<FCDA\b([^>]*?)\/>/g;
        let fcdaM: RegExpExecArray|null;

        while ((fcdaM = fcdaRe.exec(dsBody))) {
          const attrs  = fcdaM[1];
          const ga = (n: string) => new RegExp(`\\b${n}="([^"]*)"`, "").exec(attrs)?.[1] ?? "";
          const fLd  = ga("ldInst"), fPfx = ga("prefix"),
                fCls = ga("lnClass"), fIn  = ga("lnInst"),
                fDo  = ga("doName"),  fFc  = ga("fc");
          if (!fCls || !fDo) continue;
          const key = `${datasetName}|${iedName}|${fLd}|${fPfx}|${fCls}|${fIn}|${fDo}`;
          if (!seen.has(key)) {
            seen.add(key);
            // El doName puede ser compuesto (A.phsA) — guardar ambos
          const fDoBase = fDo.split(".")[0]; // parte base: A
          nodes.push({
              iedName, ldInst:fLd, prefix:fPfx, lnClass:fCls,
              lnInst:fIn, doiName:fDo, doiBase:fDoBase, fc:fFc, dataset:datasetName,
              mmsPath:`${fLd}/${fPfx}${fCls}${fIn}.${fDo}`,
            } as SclNode);
          // Si el doName tiene DA (A.phsA ≠ A), añadir también la entrada con doName base
          if (fDoBase !== fDo) {
            const key2 = `${datasetName}|${iedName}|${fLd}|${fPfx}|${fCls}|${fIn}|${fDoBase}`;
            if (!seen.has(key2)) {
              seen.add(key2);
              nodes.push({
                iedName, ldInst:fLd, prefix:fPfx, lnClass:fCls,
                lnInst:fIn, doiName:fDoBase, doiBase:fDoBase, fc:fFc, dataset:datasetName,
                mmsPath:`${fLd}/${fPfx}${fCls}${fIn}.${fDoBase}`,
              } as SclNode);
            }
          }
          }
        }
      }
    }
  }
  return nodes;
}
